/**
 * Agent chat provider + hooks.
 *
 * Contracts:
 * - Provider contexts hold stable references only (ids, store ref).
 * - Messages/pending state are derived via hooks from persisted messages + streaming overlays.
 * - Streaming overlays are provider-owned (platform renderers should treat them as read-only).
 *
 * Cross-platform lifecycle: `packages/core-ui/src/ai/agent/FLOW.md`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import { z } from "zod/v4";
import { convertAsyncIteratorToReadableStream } from "@ai-sdk/provider-utils";
import { RPCLink } from "@orpc/client/websocket";
import { createORPCClient, onError } from "@orpc/client";
import type { ContractRouterClient } from "@orpc/contract";
import { readUIMessageStream, type UIMessage, type UIMessageChunk } from "ai";
import { queries, mutators } from "@corpuscomplete/core/zero";
import { AgentSession } from "@corpuscomplete/core/ai/agent-session";
import { createId } from "@corpuscomplete/core/utils";
import { createStore, useStore as useZustandStore, type StoreApi } from "zustand";
import { DraftUI } from "../../../common/draft";

export namespace AgentChat {
  export type WebSocketLike = Pick<WebSocket, "addEventListener" | "send" | "readyState" | "close">;

  export type IdContextValue = {
    userId: string;
    agentType: string;
    agentVersion: number;
    sessionId: string;
    canEditSession: boolean;
  };

  export type AgentChatStore = {
    streamedMessagesById: Record<string, UIMessage>;
  };

  export type MessagesState = {
    messages: AgentSession.Message[];
    isLoading: boolean;
  };

  export type PendingToolStateView = {
    lastAssistantMessage: AgentSession.Message | undefined;
    lastAssistantMessageStatus: AgentSession.Message["status"] | "pending_tool_call";
    pendingParts: AgentSession.Message["parts"];
    isStreaming: boolean;
    hasPendingActions: boolean;
    persistedPendingToolPartKeys: Set<string>;
  };

  export type InputComponentProps = {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    disabled: boolean;
    placeholder?: string;
    showStop: boolean;
    onStop: () => void;
  };

  export type LastAssistantMessageStatus = AgentSession.Message["status"] | "pending_tool_call";

  export type UseStopReturn = {
    stop: () => void;
    canStop: boolean;
  };

  export type ProviderProps = {
    userId: string;
    agentType: string;
    agentVersion: number;
    sessionId: string;
    canEditSession?: boolean;
    createWebSocket: (url: string) => WebSocketLike;
    children: ReactNode;
  };

  export type UIMessageWithStatus<T extends UIMessage = UIMessage> = T & {
    status: AgentSession.Message["status"];
  };

  export type UseMessageStateArgs = {
    message: AgentSession.Message;
    onStreamUpdate?: () => void;
  };

  export type MessageState<T extends UIMessage = UIMessage> = {
    liveMessage: UIMessageWithStatus<T>;
    isStreaming: boolean;
    shouldShowStreamingIndicator: boolean;
  };

  export type PendingToolState = {
    liveLastAssistantMessage: UIMessageWithStatus | undefined;
    pendingParts: AgentSession.Message["parts"];
    persistedPendingToolPartKeys: Set<string>;
  };

  export function isInputDisabledForStatus(status: LastAssistantMessageStatus): boolean {
    return status === "streaming" || status === "pending_tool_call";
  }

  const IdContext = createContext<IdContextValue | null>(null);
  const StoreContext = createContext<StoreApi<AgentChatStore> | null>(null);

  function createAgentChatStore(): StoreApi<AgentChatStore> {
    return createStore<AgentChatStore>(() => ({ streamedMessagesById: {} }));
  }

  export function useIds(): IdContextValue {
    const context = useContext(IdContext);
    if (!context) {
      throw new Error("AgentChat.useIds must be used within AgentChat.Provider");
    }
    return context;
  }

  export function useStore<T>(selector: (state: AgentChatStore) => T): T {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error("AgentChat.useStore must be used within AgentChat.Provider");
    }
    return useZustandStore(store, selector);
  }

  export function useMessages(): MessagesState {
    const { agentType, agentVersion, sessionId } = useIds();
    const [messages, messagesDetails] = useQuery(
      queries.agentSession.messagesBySessionId({
        agentType,
        agentVersion,
        sessionId,
      }),
    );

    return useMemo(
      () => ({
        messages: AgentSession.normalizeMessages(messages),
        isLoading: messagesDetails.type === "unknown",
      }),
      [messages, messagesDetails.type],
    );
  }

  export function useLastAssistantMessage(): AgentSession.Message | undefined {
    const { messages } = useMessages();
    const streamedMessagesById = useStore((state) => state.streamedMessagesById);

    return useMemo(
      () => selectLastAssistantMessage(messages, streamedMessagesById),
      [messages, streamedMessagesById],
    );
  }

  export function selectLastAssistantMessage(
    messages: readonly AgentSession.Message[],
    streamedMessagesById: Record<string, UIMessage> = {},
  ): AgentSession.Message | undefined {
    return (
      findLastAssistantMessageWithPendingTools(messages, streamedMessagesById) ??
      findLastAssistantMessage(messages)
    );
  }

  export function useStreamedMessage(messageId: string): UIMessage | undefined {
    return useStore((state) => state.streamedMessagesById[messageId]);
  }

  export function usePendingToolState(): PendingToolStateView {
    const lastAssistantMessage = useLastAssistantMessage();
    const streamedLastAssistantMessage = useStore((state) =>
      lastAssistantMessage?.status === "streaming"
        ? state.streamedMessagesById[lastAssistantMessage.id]
        : undefined,
    );

    const { pendingParts, persistedPendingToolPartKeys } = useMemo(
      () =>
        derivePendingToolState({
          lastAssistantMessage,
          streamedLastAssistantMessage,
        }),
      [lastAssistantMessage, streamedLastAssistantMessage],
    );

    const lastAssistantMessageStatus =
      AgentSession.lastAssistantMessageStatus(lastAssistantMessage);
    const hasPendingActions = pendingParts.length > 0;

    return useMemo(
      () => ({
        lastAssistantMessage,
        lastAssistantMessageStatus,
        pendingParts,
        isStreaming: lastAssistantMessageStatus === "streaming",
        hasPendingActions,
        persistedPendingToolPartKeys,
      }),
      [
        lastAssistantMessage,
        lastAssistantMessageStatus,
        pendingParts,
        hasPendingActions,
        persistedPendingToolPartKeys,
      ],
    );
  }

  /**
   * Returns a stable predicate for checking whether a pending tool part is persisted.
   *
   * This is used to gate tool interactions when tool parts appear in the streaming overlay before
   * they exist in the persisted message.
   */
  export function useIsToolPartPersisted(): (messageId: string, toolCallId: string) => boolean {
    const { persistedPendingToolPartKeys } = usePendingToolState();

    return useCallback(
      (messageId: string, toolCallId: string) =>
        persistedPendingToolPartKeys.has(`${messageId}:${toolCallId}`),
      [persistedPendingToolPartKeys],
    );
  }

  export function derivePendingToolState({
    lastAssistantMessage,
    streamedLastAssistantMessage,
  }: {
    lastAssistantMessage: AgentSession.Message | undefined;
    streamedLastAssistantMessage: UIMessage | undefined;
  }): PendingToolState {
    const liveLastAssistantMessage = getLiveAssistantMessage(
      lastAssistantMessage,
      streamedLastAssistantMessage,
    );

    const pendingParts = AgentSession.getPendingToolPartsFromParts(liveLastAssistantMessage?.parts);

    const persistedPendingToolPartKeys = !lastAssistantMessage
      ? new Set<string>()
      : new Set(
          AgentSession.getPendingToolPartsFromParts(lastAssistantMessage.parts)
            .map((part) => {
              if (!("toolCallId" in part)) {
                return null;
              }
              return `${lastAssistantMessage.id}:${part.toolCallId}`;
            })
            .filter((key): key is string => key !== null),
        );

    return {
      liveLastAssistantMessage,
      pendingParts,
      persistedPendingToolPartKeys,
    };
  }

  export function Provider({
    userId,
    agentType,
    agentVersion,
    sessionId,
    canEditSession = true,
    createWebSocket,
    children,
  }: ProviderProps) {
    const storeRef = useRef<StoreApi<AgentChatStore> | null>(null);
    if (!storeRef.current) {
      storeRef.current = createAgentChatStore();
    }
    const store = storeRef.current;

    const [messages] = useQuery(
      queries.agentSession.messagesBySessionId({
        agentType,
        agentVersion,
        sessionId,
      }),
    );
    const normalizedMessages = useMemo(() => AgentSession.normalizeMessages(messages), [messages]);

    const lastAssistantMessage = useMemo(
      () => findLastAssistantMessage(normalizedMessages),
      [normalizedMessages],
    );

    const streamingMessage = useMemo(() => {
      if (!lastAssistantMessage || lastAssistantMessage.status !== "streaming") {
        return undefined;
      }

      return {
        id: lastAssistantMessage.id,
        sessionId: lastAssistantMessage.sessionId,
        parts: lastAssistantMessage.parts,
      };
    }, [lastAssistantMessage]);

    useEffect(() => {
      if (!streamingMessage) {
        store.setState((state) =>
          Object.keys(state.streamedMessagesById).length === 0
            ? state
            : { streamedMessagesById: {} },
        );
        return;
      }

      const path = `/agent/${agentType}/session/${streamingMessage.sessionId}/message/${streamingMessage.id}`;
      const controller = new AbortController();
      const websocket = createWebSocket(path);
      const link = new RPCLink({
        websocket,
        interceptors: [
          onError((error) => {
            console.error(error);
          }),
        ],
      });
      const client: ContractRouterClient<typeof AgentSession.messageStreamerContract> =
        createORPCClient(link);

      store.setState((state) =>
        Object.keys(state.streamedMessagesById).length === 0 ? state : { streamedMessagesById: {} },
      );

      void (async () => {
        const rawStream = await client.subscribe(undefined, {
          signal: controller.signal,
        });
        const uiChunkStream = convertAsyncIteratorToReadableStream(
          rawStream,
        ).pipeThrough<UIMessageChunk>(
          new TransformStream({
            transform({ chunk }, streamController) {
              streamController.enqueue(chunk);
            },
          }),
        );
        const uiMessageStream = readUIMessageStream({
          stream: uiChunkStream,
          message:
            streamingMessage.parts.length > 0
              ? ({ ...streamingMessage, role: "assistant" } as unknown as UIMessage)
              : undefined,
        });

        for await (const streamedMessage of uiMessageStream) {
          store.setState({ streamedMessagesById: { [streamingMessage.id]: streamedMessage } });
        }
      })();

      return () => {
        controller.abort();
        websocket.close();
        store.setState((state) => {
          if (!(streamingMessage.id in state.streamedMessagesById)) {
            return state;
          }
          const next = { ...state.streamedMessagesById };
          delete next[streamingMessage.id];
          return { streamedMessagesById: next };
        });
      };
    }, [agentType, createWebSocket, store, streamingMessage]);

    const idValue = useMemo<IdContextValue>(
      () => ({
        userId,
        agentType,
        agentVersion,
        sessionId,
        canEditSession,
      }),
      [userId, agentType, agentVersion, sessionId, canEditSession],
    );

    return (
      <IdContext.Provider value={idValue}>
        <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
      </IdContext.Provider>
    );
  }

  export function deriveMessageState<T extends UIMessage>({
    message,
    streamedMessage,
  }: {
    message: AgentSession.Message;
    streamedMessage: UIMessage | undefined;
  }): MessageState<T> {
    const liveMessage = (
      streamedMessage
        ? ({ ...streamedMessage, status: "streaming" } as UIMessageWithStatus<T>)
        : (message as UIMessageWithStatus<T>)
    ) as UIMessageWithStatus<T>;
    const isStreaming = liveMessage.status === "streaming";
    const newPartsCount = liveMessage.parts.length - message.parts.length;

    return {
      liveMessage,
      isStreaming,
      shouldShowStreamingIndicator: isStreaming && newPartsCount === 0,
    };
  }

  export function useMessageState<T extends UIMessage>({
    message,
    onStreamUpdate,
  }: UseMessageStateArgs): MessageState<T> {
    const streamedMessage = useStreamedMessage(message.id);
    const messageState = useMemo(
      () => deriveMessageState<T>({ message, streamedMessage }),
      [message, streamedMessage],
    );

    useEffect(() => {
      if (messageState.isStreaming) {
        onStreamUpdate?.();
      }
    }, [messageState, onStreamUpdate]);

    return messageState;
  }

  const inputSchema = z.string();

  export function useInput() {
    const { lastAssistantMessageStatus } = usePendingToolState();
    const { userId, agentType, sessionId, canEditSession } = useIds();
    const zero = useZero();

    const draftKey = `agentChat.${sessionId}`;
    const {
      state: input,
      set: setInput,
      reset: resetInput,
    } = DraftUI.useDraft(userId, draftKey, inputSchema, () => "");

    const submit = useCallback(() => {
      if (!canEditSession) return;
      if (!input.trim()) return;

      zero.mutate(
        mutators.agentSession.sendMessage({
          agentType,
          messageId: createId(),
          text: input.trim(),
          sessionId,
          responseMessageId: createId(),
        }),
      );
      resetInput();
    }, [input, zero, agentType, sessionId, resetInput, canEditSession]);

    const disabled = !canEditSession || isInputDisabledForStatus(lastAssistantMessageStatus);

    return useMemo(
      () => ({
        input,
        setInput,
        submit,
        reset: resetInput,
        disabled,
      }),
      [input, setInput, submit, resetInput, disabled],
    );
  }

  export function useStop(): UseStopReturn {
    const lastAssistantMessage = useLastAssistantMessage();
    const { agentType, sessionId, canEditSession } = useIds();
    const zero = useZero();

    const stop = useCallback(() => {
      if (!canEditSession) return;
      if (!lastAssistantMessage) return;

      zero.mutate(
        mutators.agentSession.cancelMessage({
          agentType,
          sessionId,
          messageId: lastAssistantMessage.id,
        }),
      );
    }, [zero, agentType, sessionId, lastAssistantMessage, canEditSession]);

    const canStop =
      canEditSession && !!lastAssistantMessage && lastAssistantMessage.status === "streaming";

    return useMemo(() => ({ stop, canStop }), [stop, canStop]);
  }

  function findLastAssistantMessage(
    messages: readonly AgentSession.Message[],
  ): AgentSession.Message | undefined {
    for (const message of messages) {
      if (message?.role === "assistant") {
        return message;
      }
    }
    return undefined;
  }

  function getLiveAssistantMessage(
    message: AgentSession.Message | undefined,
    streamedMessage: UIMessage | undefined,
  ): (UIMessage & { status: AgentSession.Message["status"] }) | undefined {
    if (!message) {
      return undefined;
    }

    if (message.status !== "streaming" || !streamedMessage) {
      return message as UIMessage & { status: AgentSession.Message["status"] };
    }

    return { ...streamedMessage, status: "streaming" } as UIMessage & {
      status: AgentSession.Message["status"];
    };
  }

  function findLastAssistantMessageWithPendingTools(
    messages: readonly AgentSession.Message[],
    streamedMessagesById: Record<string, UIMessage>,
  ): AgentSession.Message | undefined {
    for (const message of messages) {
      if (message?.role !== "assistant") {
        continue;
      }

      const liveMessage = getLiveAssistantMessage(message, streamedMessagesById[message.id]);
      if (AgentSession.getPendingToolPartsFromParts(liveMessage?.parts).length > 0) {
        return message;
      }
    }

    return undefined;
  }
}
